// ============================================================================
// Provider-role API helpers — Laboratory, Radiology, Pharmacy.
//
// Post-2026-04 refactor: LabOrder / ImagingOrder / Prescription are all
// "header + items[]" shapes. Status transitions happen via PATCH on the
// detail endpoint. The backend scopes the list endpoint by caller role —
// each provider sees only orders routed to their organization.
//
// The backend routes we use here:
//   GET   /api/clinical-notes/lab-orders/               list
//   GET   /api/clinical-notes/lab-orders/{id}/          detail
//   PATCH /api/clinical-notes/lab-orders/{id}/ { status, result_summary, result_json }
//
//   GET   /api/clinical-notes/imaging-orders/           list
//   GET   /api/clinical-notes/imaging-orders/{id}/      detail
//   PATCH /api/clinical-notes/imaging-orders/{id}/ { status, report_summary? }
//
//   GET   /api/clinical-notes/prescriptions/            list
//   GET   /api/clinical-notes/prescriptions/{id}/       detail
//   PATCH /api/clinical-notes/prescriptions/{id}/ { status, notes? }
//
// When the backend grows a dedicated queue or status-transition endpoint
// per modality, point these helpers at it.
// ============================================================================

import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

export async function getLabQueue() {
  const { data } = await api.get(endpoints.labOrderList, {
    params: { ordering: '-created_at' },
  })
  return unwrapList(data)
}

export async function getLabOrder(id) {
  const { data } = await api.get(endpoints.labOrderDetail(id))
  return data
}

export async function updateLabStatus(id, status, comment) {
  // `comment` is not modeled on the order itself; if we need lab-side comments
  // we should add a LabOrderNote table. For now the comment is dropped.
  const body = { status }
  if (comment) body.clinical_notes = comment
  const { data } = await api.patch(endpoints.labOrderDetail(id), body)
  return data
}

/**
 * Update lab-order result fields. The lab tech can call this with any
 * combination of:
 *   - summary             → result_summary (legacy / final-summary text)
 *   - prelim              → preliminary_result_summary (early read)
 *   - comment             → result_comment (lab flags)
 *   - result_status       → 'pending' | 'preliminary' | 'final'
 *   - value_json          → structured analyte values (existing)
 * Setting result_status='final' also flips order.status='completed' so
 * the doctor's panel shows the green Final badge.
 */
export async function postLabResult(id, opts = {}) {
  const { summary, prelim, comment, result_status, value_json } = opts
  const body = {}
  if (summary !== undefined) body.result_summary = summary
  if (prelim !== undefined)  body.preliminary_result_summary = prelim
  if (comment !== undefined) body.result_comment = comment
  if (result_status !== undefined) {
    body.result_status = result_status
    if (result_status === 'final') body.status = 'completed'
  }
  if (value_json !== undefined) body.result_json = value_json
  const { data } = await api.patch(endpoints.labOrderDetail(id), body)
  return data
}

// ---- Radiology ------------------------------------------------------------

export async function getImagingQueue() {
  const { data } = await api.get(endpoints.imagingOrderList, {
    params: { ordering: '-created_at' },
  })
  return unwrapList(data)
}

export async function getImagingOrder(id) {
  const { data } = await api.get(`${endpoints.imagingOrderList}${id}/`)
  return data
}

export async function updateImagingStatus(id, status, notes) {
  const body = { status }
  if (notes) body.clinical_indication = notes
  const { data } = await api.patch(`${endpoints.imagingOrderList}${id}/`, body)
  return data
}

/**
 * Update imaging-order result fields. Mirrors postLabResult shape.
 *   - summary             → report_summary (final report)
 *   - prelim              → preliminary_imaging_summary (early read)
 *   - impression          → impression (radiologist's bottom line)
 *   - comment             → result_comment (technique notes / flags)
 *   - result_status       → 'pending' | 'preliminary' | 'final'
 */
export async function postImagingReport(id, opts = {}) {
  const { summary, prelim, impression, comment, result_status } = opts
  const body = {}
  if (summary !== undefined)    body.report_summary = summary
  if (prelim !== undefined)     body.preliminary_imaging_summary = prelim
  if (impression !== undefined) body.impression = impression
  if (comment !== undefined)    body.result_comment = comment
  if (result_status !== undefined) {
    body.result_status = result_status
    if (result_status === 'final') body.status = 'completed'
  }
  const { data } = await api.patch(`${endpoints.imagingOrderList}${id}/`, body)
  return data
}

/**
 * Pharmacy updates a single PrescriptionItem's dispensing fields.
 * Accepts any subset of:
 *   dispensing_status, dispensed_quantity, substituted_drug_name,
 *   unavailable_reason, pharmacist_comment.
 */
export async function updatePrescriptionItem(itemId, patch) {
  const { data } = await api.patch(
    `/api/clinical-notes/prescription-items/${itemId}/`, patch,
  )
  return data
}

/**
 * Lab tech updates a single LabOrderItem's per-test result fields.
 * Accepts any subset of:
 *   result_value, result_unit, reference_range, abnormal_flag.
 *
 * abnormal_flag must be one of '', 'L', 'H', 'LL', 'HH', 'A':
 *   ''      in range / no flag
 *   L / H   below / above reference range
 *   LL / HH critically low / high (panic value)
 *   A       qualitative-abnormal — e.g. malaria RDT positive when ref = negative
 *
 * Backend returns the updated item; the FE typically uses it to
 * replace the row in the parent LabOrder cache so the on-screen
 * row reflects the saved state.
 */
export async function updateLabOrderItem(itemId, patch) {
  const { data } = await api.patch(
    `/api/clinical-notes/lab-order-items/${itemId}/`, patch,
  )
  return data
}

/**
 * Lab tech updates a single LabOrderItemAnalyte's per-analyte fields.
 * Accepts any subset of:
 *   result_value, result_unit, reference_range, abnormal_flag.
 *
 * Used by the Test Results table on LabOrderDetail when a panel order
 * (FBC, UEC, LFT, etc.) has been auto-expanded into its constituent
 * analyte rows. Each row PATCHes its own analyte on blur, so the lab
 * tech can fill in Hb, WBC, RBC… and each save closes its own loop.
 */
export async function updateLabOrderItemAnalyte(analyteId, patch) {
  const { data } = await api.patch(
    `/api/clinical-notes/lab-order-item-analytes/${analyteId}/`, patch,
  )
  return data
}

// ---- Pharmacy -------------------------------------------------------------

export async function getPharmacyQueue() {
  const { data } = await api.get(endpoints.prescriptionList, {
    params: { ordering: '-created_at' },
  })
  return unwrapList(data)
}

export async function getPrescription(id) {
  const { data } = await api.get(`${endpoints.prescriptionList}${id}/`)
  return data
}

export async function updatePrescriptionStatus(id, status, notes) {
  const body = { status }
  if (notes) body.notes = notes
  const { data } = await api.patch(`${endpoints.prescriptionList}${id}/`, body)
  return data
}
